import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { loadFansAudioPlayerResources } from '../audio/fansAudioPlayerLoader';

interface PressingTrack {
  order: number;
  trackId: number;
  title: string;
  durationSeconds: number | null;
  streamUrl: string | null;
  lyrics: unknown[] | null;
  hasLyrics: boolean;
}

interface PressingMultiTrackPlayerProps {
  tracks: PressingTrack[];
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  playRequestId?: number;
}

export default function PressingMultiTrackPlayer({ tracks, selectedIndex, onSelectedIndexChange, playRequestId = 0 }: PressingMultiTrackPlayerProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<any>(null);
  const [resourcesReady, setResourcesReady] = useState<boolean>(typeof window !== 'undefined' && !!window.FansAudioPlayer);
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  const currentTrack = tracks[selectedIndex] || null;
  const hasPrevious = selectedIndex > 0;
  const hasNext = selectedIndex < tracks.length - 1;

  useEffect(() => {
    if (!playRequestId) return;
    setShouldAutoStart(true);
  }, [playRequestId]);

  useEffect(() => {
    let cancelled = false;
    loadFansAudioPlayerResources().then(() => {
      if (!cancelled) {
        setResourcesReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!resourcesReady || !mountRef.current || !window.FansAudioPlayer) return;

    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }

    if (!currentTrack?.streamUrl) {
      mountRef.current.innerHTML = '';
      return;
    }

    const trackData = {
      src: currentTrack.streamUrl,
      title: currentTrack.title,
      format: 'aac',
      lyrics: currentTrack.hasLyrics ? (currentTrack.lyrics || []) : [],
    };

    const showTrackNav = tracks.length > 1;

    playerRef.current = new window.FansAudioPlayer(mountRef.current, trackData, {
      autoStart: shouldAutoStart,
      overlayPlayPause: true,
      repeat: false,
      compact: true,
      fullWidth: true,
      hideButtons: ['rewind', 'stop'],
      extraButtons: showTrackNav ? [
        {
          icon: 'fa-solid fa-backward-step',
          label: 'Previous track',
          classes: !hasPrevious ? 'fap-btn-disabled' : '',
          disabled: !hasPrevious,
          onClick: () => {
            if (!hasPrevious) return;
            setShouldAutoStart(true);
            onSelectedIndexChange(selectedIndex - 1);
          },
        },
        {
          icon: 'fa-solid fa-forward-step',
          label: 'Next track',
          classes: !hasNext ? 'fap-btn-disabled' : '',
          disabled: !hasNext,
          onClick: () => {
            if (!hasNext) return;
            setShouldAutoStart(true);
            onSelectedIndexChange(selectedIndex + 1);
          },
        },
      ] : [],
      onEnded: () => {
        if (selectedIndex < tracks.length - 1) {
          setShouldAutoStart(true);
          onSelectedIndexChange(selectedIndex + 1);
        } else {
          setShouldAutoStart(false);
        }
      },
    });

    playerRef.current.mount();
    setShouldAutoStart(false);

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
    }, [currentTrack?.trackId, currentTrack?.streamUrl, currentTrack?.title, currentTrack?.hasLyrics, currentTrack?.lyrics, hasNext, hasPrevious, onSelectedIndexChange, resourcesReady, selectedIndex, shouldAutoStart, tracks.length]);

  return (
    <>
      {!resourcesReady ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-electric-cyan animate-spin" />
        </div>
      ) : currentTrack?.streamUrl ? (
        <div ref={mountRef} />
      ) : (
        <div className="h-28 flex items-center justify-center text-sm text-white/60 rounded-xl border border-white/10 bg-black/20">
          This track does not currently have a playable stream.
        </div>
      )}
    </>
  );
}
